import { useCallback } from 'react';
import { useRouter } from 'next/router';

import { Routes } from '~/domains/common/constants/routes';

import type { ObserveMarketGroupRef } from '../../hooks/useMarketGroupVisibility';
import { usePriceFormat } from '../../state/marketSettings.store';
import { getFormattedPrice } from '../../utils/pricing';
import {
  reportPredictionsWidgetGameDetailsTapped,
  reportPredictionsWidgetMarketTapped,
  type PredictionsWidgetPlacement,
} from '../../analyticsEvents';
import { MarketCard } from '../MarketCard/MarketCard';
import type { MarketType, TeamType } from '../MarketCard/types';

import { readTapContext } from './readTapContext';
import { WIDGET_LOCATION } from './constants';

type PromotedMarketCardProps = {
  marketGroupId: string;
  placement: PredictionsWidgetPlacement;
  observeMarketGroup: ObserveMarketGroupRef;
};

export function PromotedMarketCard({
  marketGroupId,
  placement,
  observeMarketGroup,
}: PromotedMarketCardProps) {
  const router = useRouter();
  const priceFormat = usePriceFormat();
  const observeRef = observeMarketGroup(marketGroupId);

  const handleCardClick = useCallback(() => {
    const { gameId, league } = readTapContext(marketGroupId);
    reportPredictionsWidgetGameDetailsTapped({
      location: WIDGET_LOCATION,
      gameId,
      league,
      placement,
    });
  }, [marketGroupId, placement]);

  // Starting checkout from the homepage is not permitted, so an odds cell navigates to the
  // market detail rather than opening the selection drawer.
  const handleMarketClick = useCallback(
    (market: MarketType, team: TeamType) => {
      const { group, gameId, league, gameState } = readTapContext(marketGroupId);
      const line = team === 'home' ? group?.homeTeamLine : group?.awayTeamLine;

      reportPredictionsWidgetMarketTapped({
        location: WIDGET_LOCATION,
        gameId,
        league,
        placement,
        selection: `${market}_${team}`,
        odds: getFormattedPrice(line?.[market]?.price, priceFormat),
        gameState,
      });

      router.push(Routes.marketsEvent(marketGroupId));
    },
    [marketGroupId, placement, priceFormat, router]
  );

  return (
    <MarketCard
      ref={observeRef}
      marketGroupId={marketGroupId}
      onClick={handleCardClick}
      onMarketClick={handleMarketClick}
    />
  );
}
